import { Link } from "react-router-dom";
import SunAccent from "@/components/feature/SunAccent";
import Reveal from "@/components/base/Reveal";
import type { ExperienceDetail } from "@/mocks/experienceDetails";
import type { ExperienceSummary } from "@/lib/publicCatalog";

const splitTokens = (value: string): string[] =>
  value
    .split("•")
    .map((token) => token.trim().toLowerCase())
    .filter(Boolean);

/**
 * Ranks the other experiences by how closely they relate to the current one —
 * experiences that share highlights score highest, followed by experiences that
 * suit the same kind of visitor — so the suggestions genuinely match what you
 * just explored.
 */
function getRelatedExperiences(
  all: ExperienceSummary[],
  current: ExperienceSummary,
  limit = 3,
): ExperienceSummary[] {
  const currentAudience = splitTokens(current.perfectFor);
  return all
    .filter((item) => item.slug !== current.slug)
    .map((item) => ({
      item,
      score:
        item.highlights.filter((highlight) => current.highlights.includes(highlight)).length +
        splitTokens(item.perfectFor).filter((audience) => currentAudience.includes(audience))
          .length,
    }))
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((entry) => entry.item);
}

interface RelatedExperiencesProps {
  currentSlug: string;
  experiences: ExperienceSummary[];
  details: Record<string, ExperienceDetail>;
}

export default function RelatedExperiences({
  currentSlug,
  experiences,
  details,
}: RelatedExperiencesProps) {
  const current = experiences.find((item) => item.slug === currentSlug);
  const related = current
    ? getRelatedExperiences(experiences, current)
    : experiences.filter((item) => item.slug !== currentSlug).slice(0, 3);

  if (related.length === 0) {
    return null;
  }

  return (
    <section className="sun-glaze bg-background-100 py-20 md:py-24">
      <div className="w-full px-4 md:px-8">
        <Reveal className="max-w-3xl mb-10 md:mb-12">
          <div className="flex items-center gap-3 mb-5">
            <SunAccent circleClassName="w-8 h-8" iconClassName="text-base" /><span className="h-px w-10 bg-accent-500"></span>
            <span className="text-xs font-semibold tracking-[0.24em] uppercase text-accent-600">
              Keep Exploring
            </span>
          </div>
          <h2 className="font-heading text-3xl md:text-4xl leading-tight text-foreground-950 font-semibold">
            Related Experiences You May Love
          </h2>
          <p className="mt-5 text-base md:text-lg text-foreground-700 leading-relaxed">
            More ways to spend a day in the fields — picked because they share the activities you
            just saw and suit the same kind of traveller.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {related.map((item, index) => {
            const detail = details[item.slug];
            const badge = detail ? detail.badge : item.perfectFor;
            const meta = detail ? `${detail.duration} · ${detail.groupSize}` : item.perfectFor;
            const image = detail ? detail.heroImage : "";

            return (
              <Reveal key={item.slug} delay={index * 80} className="h-full">
                <article className="h-full flex flex-col bg-background-50 rounded-lg border border-background-200 overflow-hidden transition-colors hover:border-accent-300">
                  <Link
                    to={`/experiences/${item.slug}`}
                    className="relative block w-full h-44 md:h-48 overflow-hidden cursor-pointer"
                    aria-label={`View ${item.title}`}
                  >
                    {image ? (
                      <img
                        src={image}
                        width={1200}
                        height={800}
                        alt={`${item.title} — ${item.tagline} farm experience in Konaseema`}
                        title={`${item.title} - Rythugaru farm experience`}
                        className="w-full h-full object-cover object-top"
                        decoding="async"
                      />
                    ) : null}
                    <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-background-50/90 text-foreground-900 text-xs font-semibold px-3 py-1.5 rounded-full">
                      <span className="text-base leading-none">{item.emoji}</span>
                      {badge}
                    </span>
                  </Link>

                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="font-heading text-lg md:text-xl text-foreground-950 font-semibold">
                      <Link
                        to={`/experiences/${item.slug}`}
                        className="hover:text-primary-700 transition-colors cursor-pointer"
                      >
                        {item.title}
                      </Link>
                    </h3>
                    <p className="text-xs font-semibold uppercase tracking-wider text-accent-600 mt-1.5">
                      {item.tagline}
                    </p>
                    <p className="text-xs text-foreground-600 mt-2">{meta}</p>
                    <p className="text-sm text-foreground-700 leading-relaxed mt-3 flex-1">
                      {item.description}
                    </p>

                    <div className="flex flex-wrap gap-2 mt-4">
                      {item.highlights.slice(0, 2).map((highlight) => (
                        <span
                          key={highlight}
                          className="text-xs font-medium bg-secondary-100 text-secondary-900 border border-secondary-200 px-2.5 py-1 rounded-full"
                        >
                          {highlight}
                        </span>
                      ))}
                    </div>

                    <Link
                      to={`/experiences/${item.slug}`}
                      className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700 hover:text-primary-800 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      View experience
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}